import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPortfolio, ProviderErrorResponse } from '@apis/portfolio';
import type { PortfolioData } from '@interfaces/IPortfolioData';
import { portfolioKey } from '@constants/queryKeys';
import { useAppSelector } from '@store/hooks';
import { selectSiteId } from '@store/selectors/site.selectors';
import { useSelectedScheduleNumber } from '@hooks/useActiveSchedule';
import { useStaleSync } from '@hooks/useStaleSync';
import { Building2Icon } from '@utils/icons';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { MockPill } from '@components/ui/MockPill/MockPill';
import { PageBadge } from '@components/ui/PageFrame/PageFrame';
import { PortfolioSync } from './PortfolioSync/PortfolioSync';
import { KpiStrip } from './KpiStrip/KpiStrip';
import { ProjectCard } from './ProjectCard/ProjectCard';
import { AlertsRail } from './AlertsRail/AlertsRail';
import { PortfolioSkeleton } from './PortfolioSkeleton';
import { useOpenMonitor } from './hooks/useOpenMonitor';
import { useFavorites } from './hooks/useFavorites';
import styles from './styles.module.css';

/** Age past which an idle, visible portfolio auto-syncs (a catch-up, not a poll). */
const STALE_MS = 3_600_000; // 1 hour

export function PortfolioPage() {
  return (
    <>
      <PageBadge>
        <MockPill kind="badge" />
      </PageBadge>
      <PortfolioBody />
    </>
  );
}

function PortfolioBody() {
  const { t, i18n } = useTranslation();
  const openMonitor = useOpenMonitor();
  const { isFavorite, toggleFavorite } = useFavorites();
  const queryClient = useQueryClient();

  // The schedule the user has selected in the TopBar picker for the current
  // site (Redux, falling back to the persisted last-viewed). Passed to the BFF
  // so this site's card derives from the SELECTED schedule, not just the newest
  // — switching the picker on the Portfolio moves the card, matching the
  // Scheduler / Dashboard. The raw selection is sent as-is; the backend degrades
  // to the newest schedule if it's stale/unreadable, so no list validation here.
  const siteId = useAppSelector(selectSiteId);
  const activeScheduleNumber = useSelectedScheduleNumber(siteId) ?? undefined;
  const active = useMemo(
    () =>
      siteId && activeScheduleNumber ? { siteId, scheduleNumber: activeScheduleNumber } : undefined,
    [siteId, activeScheduleNumber],
  );
  // Key the cache by the selection so switching schedules is a distinct query
  // (a clean refetch with the new pin) rather than a stale hit on the old data.
  const portfolioQueryKey = useMemo(
    () => portfolioKey(siteId, activeScheduleNumber),
    [siteId, activeScheduleNumber],
  );

  // The main query is always the FAST path (no verify): fresh live KPIs +
  // schedule from the long cache, ~tens of ms. Verification against the Genba
  // source (the slow part) is a separate background step (`runVerify`) so it
  // never blocks the visible numbers.
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: portfolioQueryKey,
    queryFn: ({ signal }) => fetchPortfolio(signal, false, active),
    retry: (failureCount, err) => (err instanceof ProviderErrorResponse ? false : failureCount < 2),
    // No polling and no refetch on remount/focus. The data stays put (and the
    // network tab stays quiet) until the user hits Refresh, or one of the two
    // "you've been away" catch-ups below fires. `gcTime: Infinity` keeps the
    // last result cached across SPA navigation, so returning shows it instantly
    // (stale, no skeleton) instead of a cold reload.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    // Switching the schedule changes the query key, so the new pin is a fresh
    // query. Keep the previous card visible while it loads instead of dropping
    // to the full-page skeleton — the numbers just swap in place (skeleton only
    // on the very first, data-less load).
    placeholderData: keepPreviousData,
  });

  // "Last synced" reflects the last time data was CONFIRMED against the source.
  // `null` means "use the query's own fetch time" — correct on first load and on
  // return-to-page (the cached data's age). A Refresh freezes it (below) so the
  // fast fetch can't make it read "now" before Genba is actually checked, and a
  // completed verify advances it — so the label never tells an optimistic lie.
  const [syncedLabelIso, setSyncedLabelIso] = useState<string | null>(null);
  // Switching the selected schedule swaps the card to a fresh, UNVERIFIED fetch
  // of a different schedule, so a frozen "confirmed" timestamp from the previous
  // pin would be an optimistic lie. Drop it (adjust-state-during-render per the
  // React docs — not an effect) so the label falls back to the new data's own
  // sync time. The in-flight verify from the old pin is aborted below.
  const prevActiveRef = useRef(active);
  if (prevActiveRef.current !== active) {
    prevActiveRef.current = active;
    if (syncedLabelIso !== null) setSyncedLabelIso(null);
  }
  const syncedLabel = syncedLabelIso ?? data?.syncedAtIso ?? '';

  // Background source-verification: confirm the cached schedule against the Genba
  // version (the slow ~1.3s call when Genba is cold). The displayed data is
  // already fresh/visible; this only corrects a schedule edited DIRECTLY in Genba
  // and advances the synced label — so the spinner keeps spinning until it's done
  // and the label only then reads "now". A failed verify leaves the data + label
  // where they are (provider errors are handled by the global reactive onError).
  // Holds the in-flight verify's AbortController so a newer verify (or unmount)
  // can cancel a stale one — otherwise a late verify could write older data over
  // a fresher result, or run on after the user navigates away.
  const verifyAbortRef = useRef<AbortController | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const runVerify = useCallback(async () => {
    verifyAbortRef.current?.abort(); // supersede any in-flight verify
    const controller = new AbortController();
    verifyAbortRef.current = controller;
    setIsVerifying(true);
    try {
      const verified = await fetchPortfolio(controller.signal, true, active);
      if (controller.signal.aborted) return;
      queryClient.setQueryData(portfolioQueryKey, verified);
      setSyncedLabelIso(verified.syncedAtIso); // advance the label only now
    } catch {
      // aborted, or failed — keep showing the last good data; label stays put
    } finally {
      // Only clear if THIS verify is still the current one (a superseding verify
      // owns the spinner now).
      if (verifyAbortRef.current === controller) {
        verifyAbortRef.current = null;
        setIsVerifying(false);
      }
    }
  }, [queryClient, portfolioQueryKey, active]);

  // Abort an in-flight verify on unmount so it can't write to the cache after.
  useEffect(() => () => verifyAbortRef.current?.abort(), []);

  // Abort a verify still confirming the PREVIOUS pin when the selection changes,
  // so its late completion can't advance the label (line above resets it) over
  // the new selection's data. Pure side effect — no state written here.
  useEffect(() => {
    verifyAbortRef.current?.abort();
  }, [active]);

  // The Refresh button: update the live numbers immediately (fast refetch), then
  // verify the schedule against the source in the background — verify runs after
  // the fast fetch so it's the last writer (its corrected schedule + advanced
  // label win even if Genba happens to answer quickly).
  const refresh = useCallback(() => {
    // Freeze the label at its current confirmed time so the fast fetch's "now"
    // can't leak into it before runVerify confirms the source.
    setSyncedLabelIso(
      (prev) => prev ?? queryClient.getQueryData<PortfolioData>(portfolioQueryKey)?.syncedAtIso ?? null,
    );
    // Only verify if the fast fetch succeeded — no point firing a second request
    // (and a second failure) at an already-failing backend.
    void refetch().then((res) => {
      if (res.isSuccess) void runVerify();
    });
  }, [refetch, runVerify, queryClient, portfolioQueryKey]);

  // Auto-sync when the data is >= 1h stale → a background catch-up (just verify,
  // which also refreshes the numbers), at most once an hour. See `useStaleSync`
  // for the visibilitychange + armed-timer mechanics (both a returning-tab
  // catch-up and an always-visible-tab timer).
  useStaleSync(dataUpdatedAt, STALE_MS, () => void runVerify());

  // Render-order only (no data mutation): favorites float to the top sorted
  // alphabetically by name; the rest keep their existing backend order.
  const orderedProjects = useMemo(() => {
    const projects = data?.projects ?? [];
    const favorites = projects
      .filter((p) => isFavorite(p.id))
      .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
    const rest = projects.filter((p) => !isFavorite(p.id));
    return [...favorites, ...rest];
  }, [data?.projects, isFavorite, i18n.language]);

  const sync = { syncedAtIso: syncedLabel, onRefresh: refresh, isRefreshing: isFetching || isVerifying };

  if (isLoading) {
    return <PortfolioSkeleton />;
  }
  if (error instanceof ProviderErrorResponse) {
    // Reconnecting is handled globally by the TopBar (visible here), so this is
    // just the shared honest empty state — no in-page reconnect banner.
    return (
      <div className={styles.page}>
        <DataUnavailable />
      </div>
    );
  }
  if (isError || !data) {
    if (error instanceof Error) {
      console.error('portfolio load failed:', error.message);
    }
    return (
      <div className={styles.page}>
        <div className={styles.errorBanner} role="alert">
          <span>{t('portfolio.error.loadFailed')}</span>
          <button onClick={() => void refetch()}>{t('portfolio.error.retry')}</button>
        </div>
      </div>
    );
  }

  // The app starts empty: a tenant with no synced sites gets a friendly
  // empty state that routes to the Sync / New Site page rather than a blank
  // grid. (The backend returns an empty list here, not an error.)
  if (data.projects.length === 0) {
    return (
      <>
        <PortfolioSync {...sync} />
        <div className={styles.empty} data-testid="portfolio-empty">
          <span className={styles.emptyIcon} aria-hidden="true">
            <Building2Icon size={28} />
          </span>
          <h2 className={styles.emptyTitle}>{t('portfolio.empty.title')}</h2>
          <p className={styles.emptyBody}>{t('portfolio.empty.body')}</p>
          <Link to="/admin/project-merge" className={styles.emptyCta}>
            {t('portfolio.empty.cta')}
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <PortfolioSync {...sync} />
      <div className={styles.page}>
        <KpiStrip kpis={data.kpis} />
        <div className={styles.body}>
          <section className={styles.projects} data-testid="project-list">
            {orderedProjects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onClick={() => void openMonitor(p.id)}
                isFavorite={isFavorite(p.id)}
                onToggleFavorite={() => toggleFavorite(p.id)}
              />
            ))}
          </section>
          <AlertsRail alerts={data.alerts} />
        </div>
      </div>
    </>
  );
}
